import { useState } from "react";
import {
  DocumentsIcon,
  DownloadIcon,
  FileTextIcon,
  SecurityIcon,
  AlertIcon,
  SearchIcon,
  ZapIcon,
  AssetsIcon,
  ServicesIcon,
  ExternalLinkIcon,
  TrendingUpIcon,
} from "../../../../../shared/cockpit/components/icons/Icon";
import "./Contracts.scss";

interface ContractEntry {
  id: string;
  name: string;
  details: string;
  category: "STROM" | "IOT" | "VERSICHERUNG" | "SERVICE" | "SOFTWARE" | "TELEKOM";
  partner: string;
  status: "Active" | "Review";
  validUntil: string;
  volume: string;
}

const contractsData: ContractEntry[] = [
  {
    id: "CTR-2025-101",
    name: "Uniper Rahmenvertrag 2026",
    details: "Vollversorgung inkl. PPA-Option",
    category: "STROM",
    partner: "Uniper SE",
    status: "Active",
    validUntil: "31.12.2028",
    volume: "2.5M €/p.a.",
  },
  {
    id: "CTR-2025-102",
    name: "Vodafone M2M Pool",
    details: "Global SIMs für Ladesäulen & Gateways",
    category: "IOT",
    partner: "Vodafone",
    status: "Active",
    validUntil: "31.12.2027",
    volume: "120k €/p.a.",
  },
  {
    id: "CTR-2025-103",
    name: "Allianz Tech-Protect",
    details: "Allgefahrenversicherung PV & Speicher",
    category: "VERSICHERUNG",
    partner: "Allianz",
    status: "Review",
    validUntil: "30.06.2026",
    volume: "85k €/p.a.",
  },
  {
    id: "CTR-2025-104",
    name: "Wartung Ladeinfrastruktur",
    details: "SLA: 4h Reaktionszeit",
    category: "SERVICE",
    partner: "ChargePoint",
    status: "Active",
    validUntil: "31.12.2026",
    volume: "Variable",
  },
  {
    id: "CTR-2025-105",
    name: "Metergrid SaaS",
    details: "Mieterstrom-Abrechnungsplattform",
    category: "SOFTWARE",
    partner: "Metergrid",
    status: "Active",
    validUntil: "Monthly",
    volume: "450 €/Unit",
  },
  {
    id: "CTR-2025-106",
    name: "Telekom Glasfaser HQ",
    details: "Redundante Anbindung Rechenzentrum",
    category: "TELEKOM",
    partner: "Deutsche Telekom",
    status: "Active",
    validUntil: "31.12.2029",
    volume: "12k €/p.a.",
  },
];

const tabs = [
  "ALLE VERTRÄGE",
  "STROM",
  "IOT",
  "VERSICHERUNG",
  "SERVICE",
  "SOFTWARE",
  "TELEKOM",
];

const Contracts = () => {
  const [activeTab, setActiveTab] = useState("ALLE VERTRÄGE");

  const filteredContracts =
    activeTab === "ALLE VERTRÄGE"
      ? contractsData
      : contractsData.filter((c) => c.category === activeTab);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "STROM":
        return <ZapIcon />;
      case "IOT":
        return <AssetsIcon />;
      case "VERSICHERUNG":
        return <SecurityIcon />;
      case "SERVICE":
        return <ServicesIcon />;
      case "SOFTWARE":
        return <FileTextIcon />;
      case "TELEKOM":
        return <AssetsIcon />;
      default:
        return <DocumentsIcon />;
    }
  };

  return (
    <div className="contracts-view">
      <header className="contracts-view__header">
        <div className="contracts-view__header__content">
          <div className="contracts-view__header__title--container">
            <div className="contracts-view__header__icon-wrapper">
              <DocumentsIcon />
            </div>
            <h2 className="contracts-view__header__title">Verträge & Partner</h2>
          </div>
          <p className="contracts-view__header__subtitle">
            Zentrale Verwaltung aller Rahmenverträge (IoT, Energie, Versicherung)
            der Schmidt Gruppe.
          </p>
        </div>
        <button className="contracts-view__header__export-btn">
          <DownloadIcon /> Export Liste
        </button>
      </header>

      <section className="contracts-view__stats">
        <div className="stat-card">
          <div className="stat-card__icon stat-card__icon--blue">
            <FileTextIcon />
          </div>
          <div className="stat-card__info">
            <span className="stat-card__label">Aktive Verträge</span>
            <span className="stat-card__value">6</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card__icon stat-card__icon--purple">
            <TrendingUpIcon />
          </div>
          <div className="stat-card__info">
            <span className="stat-card__label">Gesamtvolumen (p.a.)</span>
            <span className="stat-card__value">~ 2.8M €</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card__icon stat-card__icon--orange">
            <AlertIcon />
          </div>
          <div className="stat-card__info">
            <span className="stat-card__label">Kündigungsfristen</span>
            <span className="stat-card__value">
              2 <small>kritisch</small>
            </span>
          </div>
        </div>
      </section>

      <div className="contracts-view__table-container">
        <div className="contracts-view__toolbar">
          <div className="tabs">
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="search-bar">
            <SearchIcon />
            <input type="text" placeholder="Vertrag, Partner oder ID suche" />
          </div>
        </div>

        <div className="contracts-table">
          <div className="contracts-table__header">
            <div className="contracts-table__col">VERTRAGSNAME & DETAILS</div>
            <div className="contracts-table__col">KATEGORIE</div>
            <div className="contracts-table__col">PARTNER</div>
            <div className="contracts-table__col">STATUS</div>
            <div className="contracts-table__col">LAUFZEIT BIS</div>
            <div className="contracts-table__col">VOLUMEN</div>
            <div className="contracts-table__col text-right">AKTION</div>
          </div>

          <div className="contracts-table__body">
            {filteredContracts.map((contract) => (
              <div key={contract.id} className="contracts-table__row">
                <div className="contracts-table__col">
                  <div className="contract-info">
                    <span className="contract-info__name">{contract.name}</span>
                    <span className="contract-info__details">
                      {contract.details}
                    </span>
                    <span className="contract-info__id">{contract.id}</span>
                  </div>
                </div>

                <div className="contracts-table__col">
                  <div className="category-tag">
                    {getCategoryIcon(contract.category)}
                    <span>
                      {contract.category.charAt(0) +
                        contract.category.slice(1).toLowerCase()}
                    </span>
                  </div>
                </div>

                <div className="contracts-table__col">
                  <span className="partner-name">{contract.partner}</span>
                </div>

                <div className="contracts-table__col">
                  <span
                    className={`status-pill status-pill--${contract.status.toLowerCase()}`}
                  >
                    {contract.status}
                  </span>
                </div>

                <div className="contracts-table__col">
                  <span className="date-value">{contract.validUntil}</span>
                </div>

                <div className="contracts-table__col">
                  <span className="volume-value">{contract.volume}</span>
                </div>

                <div className="contracts-table__col text-right">
                  <button className="action-btn">
                    <ExternalLinkIcon />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contracts;
